/**
 * Pure helpers for the Rahmenvertrag CSV bulk-import (UCPWO-371 / UCPWO-355).
 *
 * Everything here is framework-agnostic and side-effect free so it can be unit
 * tested in isolation. The orchestration (mutations, queries, modal state)
 * lives in `useAgreementCsvImport`.
 *
 * Spec: see `docs/adr/0001`, `docs/adr/0002` and the glossary in `CONTEXT.md`.
 * CSV format: delimiter `;`, exactly 4 columns (Nummer, Beschreibung, Preis,
 * Einheit), no header row (position based), UTF-8 with Windows-1252 fallback,
 * blank lines skipped silently.
 */

import moment from 'moment-timezone';

// ===============================================
// CONSTANTS
// ===============================================

export const CSV_DELIMITER = ';';
export const CSV_COLUMN_COUNT = 4;

/**
 * Upper bound for the uploaded file. The whole file is decoded and tokenized
 * synchronously on the main thread, so an oversized file would freeze the tab;
 * reject it up front instead. A position-based RV CSV is a few hundred KB at
 * most, so 10 MB is comfortably generous.
 */
export const MAX_CSV_BYTES = 10 * 1024 * 1024;

/** Max lengths enforced by the DB schema. */
export const MAX_BEZEICHNUNG = 45; // rahmenvertrag.bezeichnung VARCHAR(45)
export const MAX_NR = 45; // rahmenvertragsposition.nr VARCHAR(45)
export const MAX_BESCHREIBUNG = 5000; // rahmenvertragsposition.beschreibung VARCHAR(5000)

/** The canonical Einheit set (the 8 seeded `Sonstiges` rows). */
export const CANONICAL_UNITS = ['m', 'm2', 'm3', 'Stck', 'h', 'Tag', 'Pauschal'] as const;
export const FALLBACK_UNIT = 'UNBEKANNT';

/**
 * Hardcoded, case-insensitive Einheit alias map (ADR 0002). Keys are compared
 * after `.trim().toLowerCase()`; values are canonical units.
 */
export const EINHEIT_ALIASES: Record<string, string> = {
    psch: 'Pauschal',
    qm: 'm2',
    std: 'h',
    lfm: 'm',
    raum: 'Stck',
    stück: 'Stck',
};

// ===============================================
// TYPES
// ===============================================

export type IssueCategory =
    | 'priceEmpty'
    | 'priceUnparseable'
    | 'unitUnknown'
    | 'nrTruncated'
    | 'beschreibungTruncated'
    | 'positionInsertFailed';

export interface RowIssue {
    category: IssueCategory;
    /** Physical 1-based line number in the CSV file (matches Excel). */
    line: number;
    /** The original cell value that triggered the issue. */
    value: string;
    /** The row's Nummer (CSV column 1), shown so the user can locate the row. */
    nr: string;
}

export interface ParsedRow {
    /** Physical 1-based line number in the file (for reporting; matches Excel). */
    line: number;
    nr: string;
    beschreibung: string;
    preis: string;
    einheit: string;
}

/** A row resolved into the fields a `RahmenvertragspositionInput` needs (minus
 * `rahmenvertragId`, which is only known after the RV is created, and
 * `leistungId`, resolved from `einheit` at insert time). */
export interface PreparedPosition {
    line: number;
    nr: string;
    beschreibung: string;
    details: string;
    preisProEinheit: number;
    /** Canonical Einheit used to look up the `Sonstiges` leistungId. */
    einheit: string;
    issues: RowIssue[];
}

export interface PreparedImport {
    /** Positions with a non-empty (importable) price. */
    positions: PreparedPosition[];
    /** Issues that are not tied to an importable position (e.g. skipped rows). */
    rowIssues: RowIssue[];
    /** Number of non-blank data rows in the file. */
    totalRows: number;
}

export class CsvStructuralError extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'CsvStructuralError';
    }
}

// ===============================================
// DECODING
// ===============================================

/**
 * Guard against oversized uploads before the synchronous decode + tokenize
 * freezes the tab. Throws {@link CsvStructuralError} (caught by the import
 * orchestrator → error phase) when `bytes` exceeds {@link MAX_CSV_BYTES}.
 */
export function assertFileSize(bytes: number): void {
    if (bytes > MAX_CSV_BYTES) {
        const limitMb = Math.round(MAX_CSV_BYTES / (1024 * 1024));
        throw new CsvStructuralError(
            `Die Datei ist zu groß (max. ${limitMb} MB). Bitte teilen Sie sie auf.`
        );
    }
}

/**
 * Decode a CSV file's bytes as UTF-8, falling back to Windows-1252 when the
 * bytes are not valid UTF-8 (common for files exported from German Excel).
 */
export function decodeCsvBytes(buffer: ArrayBuffer): string {
    try {
        return new TextDecoder('utf-8', { fatal: true }).decode(buffer);
    } catch {
        // windows-1252 maps every byte, so this never throws.
        return new TextDecoder('windows-1252').decode(buffer);
    }
}

// ===============================================
// PARSING
// ===============================================

/**
 * Parse `;`-delimited CSV text into rows of cells. Handles double-quoted fields
 * (RFC-4180 style) so a `;` or newline inside a quoted Beschreibung does not
 * split the row. Blank lines are skipped silently.
 *
 * Throws {@link CsvStructuralError} (hard gate) when the file is empty or any
 * row does not have exactly {@link CSV_COLUMN_COUNT} columns.
 */
export function parseCsv(text: string): ParsedRow[] {
    const records = tokenize(text);

    // Drop blank lines (a record that is a single empty cell).
    const nonBlank = records.filter(
        (r) => !(r.cells.length === 1 && r.cells[0].trim() === '')
    );

    if (nonBlank.length === 0) {
        throw new CsvStructuralError('Die Datei enthält keine Daten.');
    }

    const rows: ParsedRow[] = [];
    for (const { line, cells } of nonBlank) {
        if (cells.length !== CSV_COLUMN_COUNT) {
            throw new CsvStructuralError(
                `Zeile ${line} hat ${cells.length} statt ${CSV_COLUMN_COUNT} Spalten. ` +
                    `Erwartet wird das Format: Nummer${CSV_DELIMITER}Beschreibung${CSV_DELIMITER}Preis${CSV_DELIMITER}Einheit.`
            );
        }
        rows.push({
            line,
            nr: cells[0].trim(),
            beschreibung: cells[1].trim(),
            preis: cells[2].trim(),
            einheit: cells[3].trim(),
        });
    }

    return rows;
}

/** A tokenized record plus the physical (1-based) file line where it starts. */
interface RawRecord {
    line: number;
    cells: string[];
}

/**
 * Split CSV text into records of raw cells, honouring `"`-quoted fields. Each
 * record carries the physical 1-based line number where it begins, so reported
 * line numbers match what the user sees in Excel even with blank or
 * multi-line (quoted) rows in between.
 *
 * Throws {@link CsvStructuralError} when a quoted field is never closed,
 * instead of swallowing the rest of the file into one cell (which would
 * surface later as a confusing column-count error).
 */
function tokenize(text: string): RawRecord[] {
    const records: RawRecord[] = [];
    let row: string[] = [];
    let cell = '';
    let inQuotes = false;
    let lineNo = 1; // current physical line
    let recordStartLine = 1; // physical line where the in-progress record began

    // Normalise newlines so \r\n and \r behave like \n.
    const src = text.replace(/\r\n?/g, '\n');

    for (let i = 0; i < src.length; i++) {
        const char = src[i];

        if (inQuotes) {
            if (char === '"') {
                if (src[i + 1] === '"') {
                    cell += '"';
                    i++; // skip escaped quote
                } else {
                    inQuotes = false;
                }
            } else {
                if (char === '\n') lineNo++; // a newline inside a quoted field still advances the file line
                cell += char;
            }
            continue;
        }

        if (char === '"') {
            inQuotes = true;
        } else if (char === CSV_DELIMITER) {
            row.push(cell);
            cell = '';
        } else if (char === '\n') {
            row.push(cell);
            records.push({ line: recordStartLine, cells: row });
            row = [];
            cell = '';
            lineNo++;
            recordStartLine = lineNo;
        } else {
            cell += char;
        }
    }

    if (inQuotes) {
        throw new CsvStructuralError(
            'Ein Anführungszeichen wurde geöffnet, aber nicht geschlossen. Bitte prüfen Sie die Datei auf ein fehlendes „"“.'
        );
    }

    // Flush the final cell/row (file may not end with a newline).
    row.push(cell);
    records.push({ line: recordStartLine, cells: row });

    return records;
}

// ===============================================
// PRICE
// ===============================================

export interface PriceResult {
    /** Rounded to 2 decimals (DECIMAL(10,2)). `null` means "skip the row". */
    value: number | null;
    /** True when the cell was non-empty but could not be parsed (row skipped). */
    unparseable: boolean;
}

/** German thousands-grouped integer, e.g. `1.234` or `10.000.000`. */
const THOUSANDS_GROUPED = /^-?\d{1,3}(\.\d{3})+$/;

/** A valid integer part: either a plain run of digits or thousands-grouped. */
function isValidIntegerPart(s: string): boolean {
    return /^-?\d+$/.test(s) || THOUSANDS_GROUPED.test(s);
}

/**
 * Normalize a price cell.
 * - Empty cell → `{ value: null }` (caller skips the whole row).
 * - German format `1.234,56` → `1234.56` (comma is the decimal separator).
 * - Dot-only German thousands `10.000` → `10000`; a single decimal dot `12.50`
 *   is kept as-is.
 * - Non-empty but unparseable (e.g. `auf Nachweis`, or ambiguous `1.2.3`) →
 *   `{ value: null, unparseable }`; the row is skipped (ADR 0003), because such
 *   positions have no reliable unit price and silently truncating or storing
 *   0,00 would be misleading ("free" vs. "billed on proof").
 * Negative prices are allowed.
 */
export function normalizePrice(raw: string): PriceResult {
    const trimmed = (raw ?? '').trim();
    if (trimmed === '') {
        return { value: null, unparseable: false };
    }

    // Strip currency symbols and spaces, keep digits, separators and sign.
    const cleaned = trimmed.replace(/[^\d.,-]/g, '');

    let normalized: string;
    if (cleaned.includes(',')) {
        // German decimal comma: dots are thousands separators, comma the decimal.
        const [intPart, ...rest] = cleaned.split(',');
        if (rest.length !== 1 || !isValidIntegerPart(intPart)) {
            return { value: null, unparseable: true };
        }
        normalized = `${intPart.replace(/\./g, '')}.${rest[0]}`;
    } else if (cleaned.includes('.')) {
        if (THOUSANDS_GROUPED.test(cleaned)) {
            // German thousands grouping without decimals, e.g. "10.000" → 10000.
            normalized = cleaned.replace(/\./g, '');
        } else if ((cleaned.match(/\./g) ?? []).length === 1) {
            // A single, non-grouping dot → treat as decimal point, e.g. "12.50".
            normalized = cleaned;
        } else {
            // Ambiguous multi-dot input like "1.2.3" → refuse, don't silently truncate.
            return { value: null, unparseable: true };
        }
    } else {
        normalized = cleaned;
    }

    const value = Number.parseFloat(normalized);
    if (!Number.isFinite(value)) {
        return { value: null, unparseable: true };
    }

    return { value: round2(value), unparseable: false };
}

function round2(n: number): number {
    return Math.round((n + Number.EPSILON) * 100) / 100;
}

// ===============================================
// EINHEIT
// ===============================================

export interface UnitResult {
    /** Canonical Einheit (one of CANONICAL_UNITS or FALLBACK_UNIT). */
    einheit: string;
    /** True when the raw unit could not be resolved and fell back to UNBEKANNT. */
    unknown: boolean;
}

/**
 * Resolve a raw Einheit to a canonical unit (ADR 0002).
 * Canonical-first, then the hardcoded alias map (case-insensitive + trimmed),
 * otherwise `UNBEKANNT` + flag.
 */
export function resolveEinheit(raw: string): UnitResult {
    const trimmed = (raw ?? '').trim();
    const lower = trimmed.toLowerCase();

    const canonical = CANONICAL_UNITS.find((u) => u.toLowerCase() === lower);
    if (canonical) {
        return { einheit: canonical, unknown: false };
    }

    const alias = EINHEIT_ALIASES[lower];
    if (alias) {
        return { einheit: alias, unknown: false };
    }

    return { einheit: FALLBACK_UNIT, unknown: true };
}

// ===============================================
// ROW PREPARATION
// ===============================================

/** Truncate a string to `max` chars; reports whether it was shortened. */
export function truncate(value: string, max: number): { value: string; truncated: boolean } {
    if (value.length <= max) {
        return { value, truncated: false };
    }
    return { value: value.slice(0, max), truncated: true };
}

/**
 * Turn parsed CSV rows into importable positions + collected issues.
 * Rows with an empty price cell are skipped entirely (reported as `priceEmpty`).
 */
export function prepareImport(rows: ParsedRow[]): PreparedImport {
    const positions: PreparedPosition[] = [];
    const rowIssues: RowIssue[] = [];

    for (const row of rows) {
        const price = normalizePrice(row.preis);

        // No usable price → skip the whole row. Either the cell was empty, or it
        // was non-empty but unreadable (e.g. "auf Nachweis"); both are reported
        // but neither is imported.
        if (price.value === null) {
            rowIssues.push({
                category: price.unparseable ? 'priceUnparseable' : 'priceEmpty',
                line: row.line,
                value: price.unparseable ? row.preis : row.beschreibung,
                nr: row.nr,
            });
            continue;
        }

        const issues: RowIssue[] = [];

        const unit = resolveEinheit(row.einheit);
        if (unit.unknown) {
            issues.push({ category: 'unitUnknown', line: row.line, value: row.einheit, nr: row.nr });
        }

        const nr = truncate(row.nr, MAX_NR);
        if (nr.truncated) {
            issues.push({ category: 'nrTruncated', line: row.line, value: row.nr, nr: row.nr });
        }

        const beschreibung = truncate(row.beschreibung, MAX_BESCHREIBUNG);
        if (beschreibung.truncated) {
            issues.push({
                category: 'beschreibungTruncated',
                line: row.line,
                value: row.beschreibung,
                nr: row.nr,
            });
        }

        positions.push({
            line: row.line,
            nr: nr.value,
            beschreibung: beschreibung.value,
            details: '',
            preisProEinheit: price.value,
            einheit: unit.einheit,
            issues,
        });
    }

    return { positions, rowIssues, totalRows: rows.length };
}

// ===============================================
// RV BASE DATA
// ===============================================

export interface AgreementName {
    bezeichnung: string;
    truncated: boolean;
}

/** Derive the RV bezeichnung from the filename (without `.csv`), truncated to 45. */
export function agreementNameFromFile(filename: string): AgreementName {
    const base = filename.trim().replace(/\.csv$/i, '');
    const { value, truncated } = truncate(base, MAX_BEZEICHNUNG);
    return { bezeichnung: value, truncated };
}

export interface ValidityDates {
    gueltigAb: string; // AWSDate YYYY-MM-DD
    gueltigBis: string; // AWSDate YYYY-MM-DD
}

/** gueltigAb = tomorrow, gueltigBis = tomorrow + 1 year (client-side). */
export function computeValidityDates(now: moment.Moment = moment()): ValidityDates {
    const tomorrow = now.clone().add(1, 'day');
    return {
        gueltigAb: tomorrow.format('YYYY-MM-DD'),
        gueltigBis: tomorrow.clone().add(1, 'year').format('YYYY-MM-DD'),
    };
}

// ===============================================
// REPORT LABELS
// ===============================================

export const ISSUE_LABELS: Record<IssueCategory, string> = {
    priceEmpty: 'Zeile übersprungen (kein Preis)',
    priceUnparseable: 'Zeile übersprungen (Preis nicht lesbar)',
    unitUnknown: 'Einheit unbekannt – als „Sonstiges [UNBEKANNT]“ gespeichert',
    nrTruncated: 'Nummer gekürzt (max. 45 Zeichen)',
    beschreibungTruncated: 'Beschreibung gekürzt (max. 5000 Zeichen)',
    positionInsertFailed: 'Position konnte nicht gespeichert werden',
};

/**
 * Traffic-light severity per issue (UCPWO-371 ticket feedback):
 * `error` (red) → the row is NOT imported; `warning` (yellow) → the row is
 * imported, but a value was adjusted and may need fixing afterwards.
 */
export type IssueSeverity = 'error' | 'warning';

export const ISSUE_SEVERITY: Record<IssueCategory, IssueSeverity> = {
    priceEmpty: 'error',
    priceUnparseable: 'error',
    positionInsertFailed: 'error',
    unitUnknown: 'warning',
    nrTruncated: 'warning',
    beschreibungTruncated: 'warning',
};
